// src/app/views/maintenance/MaintenancePage.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ConstructionIcon from '@mui/icons-material/Construction';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

const MaintenanceContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: theme.spacing(4, 2),
}));

const MaintenancePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  maxWidth: 800,
  width: '100%',
  boxShadow: theme.shadows[3],
  border: `1px solid ${theme.palette.primary.main}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '8px',
    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& svg': {
    fontSize: '4rem',
    color: theme.palette.primary.main,
  }
}));

const TimingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.warning.main,
  }
}));

const MaintenancePage = ({ maintenanceData }) => {
  // Format dates for display using dayjs (which is already used in the project based on App.jsx)
  const formatDate = (dateString) => {
    dayjs.locale('ko');
    return dayjs(dateString).format('YYYY년 M월 D일 (ddd) HH:mm');
  };

  // If maintenance data is missing, show generic message
  if (!maintenanceData) {
    return (
      <MaintenanceContainer>
        <MaintenancePaper>
          <IconContainer>
            <WarningAmberIcon fontSize="large" />
          </IconContainer>
          <Typography variant="h4" align="center" gutterBottom color="primary">
            시스템 점검 중
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" align="center" paragraph>
            현재 시스템 점검 중입니다. 불편을 드려 죄송합니다.
          </Typography>
          <Typography variant="body1" align="center">
            잠시 후 다시 시도해 주세요.
          </Typography>
        </MaintenancePaper>
      </MaintenanceContainer>
    );
  }

  return (
    <MaintenanceContainer>
      <MaintenancePaper>
        <IconContainer>
          <ConstructionIcon />
        </IconContainer>
        
        <Typography variant="h4" align="center" gutterBottom color="primary">
          {maintenanceData.title || '시스템 점검 중'}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <TimingContainer>
          <ScheduleIcon />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              점검 기간
            </Typography>
            <Typography variant="body2">
              {formatDate(maintenanceData.start_dt)} ~ {formatDate(maintenanceData.end_dt)}
            </Typography>
          </Box>
        </TimingContainer>
        
        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
          {maintenanceData.message || '현재 시스템 점검 중입니다. 불편을 드려 죄송합니다.'}
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary" align="center">
                © 법률저널 시험 센터
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </MaintenancePaper>
    </MaintenanceContainer>
  );
};

export default MaintenancePage;
